// A deploy replaces the hashed chunks an open tab still points at. SvelteKit already recovers a clicked
// navigation (it checks version.json and loads the new build in full), so what reaches handleError is
// a hover preload nobody saw: reported as expected, never as a fault (PostHog: 10 a day, 2026-10-09).
const STALE_CHUNK_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

export function isStaleChunkError(message: string | undefined): boolean {
  return !!message && STALE_CHUNK_RE.test(message);
}

const RELOAD_KEY = "cyborg7.staleChunkReloadAt";
const RELOAD_WINDOW_MS = 60_000;

export interface StaleChunkEnv {
  online: boolean;
  // Reaching storage itself can throw (a denied SecurityError), so it is asked for, not held.
  storage: () => Pick<Storage, "getItem" | "setItem"> | undefined;
  now: number;
  go: (href: string) => void;
}

function browserEnv(): StaleChunkEnv {
  return {
    online: typeof navigator === "undefined" || navigator.onLine !== false,
    storage: () => (typeof sessionStorage === "undefined" ? undefined : sessionStorage),
    now: Date.now(),
    go: (href) => location.assign(href),
  };
}

// The last reload's time, or "unknown" when storage cannot be read at all.
function lastReloadAt(env: StaleChunkEnv): number | "unknown" {
  try {
    const raw = env.storage()?.getItem(RELOAD_KEY);
    if (raw === undefined) return "unknown";
    return Number(raw ?? 0);
  } catch {
    // intentional: an unreadable marker is answered "unknown", and the caller fails closed.
    return "unknown";
  }
}

// Writes the marker and reads it back: only a marker that is really there may allow a reload.
function markReload(env: StaleChunkEnv): boolean {
  try {
    const storage = env.storage();
    if (!storage) return false;
    storage.setItem(RELOAD_KEY, String(env.now));
    return storage.getItem(RELOAD_KEY) === String(env.now);
  } catch {
    // intentional: an unwritable marker is answered false, and the caller fails closed.
    return false;
  }
}

// Mid-navigation: one full load of the destination recovers it; a repeat within a minute is a
// fault (the live build lacks the chunk); offline, transient. With no durable marker no reload
// runs (it could loop): the failure stays a fault.
export function recoverStaleChunk(
  destination: URL | undefined,
  env: StaleChunkEnv = browserEnv(),
): "expected" | "recovered" | "transient" | "fault" {
  if (!destination) return "expected";
  if (!env.online) return "transient";
  const last = lastReloadAt(env);
  if (last === "unknown" || env.now - last < RELOAD_WINDOW_MS) return "fault";
  if (!markReload(env)) return "fault";
  env.go(destination.href);
  return "recovered";
}
